import { CountryCode, getCountryCallingCode } from "libphonenumber-js";
import locale from "react-phone-number-input/locale/en.json";

/**
 * The countries whose passenger numbers the platform can hide behind a proxy number. The Twilio
 * Proxy pool is Australian end to end and the service runs `geo_match_level: country`, so only a
 * +61 participant can ever be assigned a proxy number.
 *
 * ISO country codes, never dial codes — dial code "1" would wrongly admit the ~20 NANP territories,
 * none of which are reachable. (The legacy `maskedCoverageCountries = ["1", "61"]` in web-app's
 * member-profile.phtml is both dial-coded and years stale; it is not the source of truth.)
 *
 * This is the single edit when coverage changes.
 */
export const MASKED_COUNTRIES: readonly CountryCode[] = ["AU"];

/** Whether a number from `country` reaches the driver as a proxy number rather than the real one. */
export const isMaskedCountry = (country?: CountryCode) => !!country && MASKED_COUNTRIES.includes(country);

/** An ISO code as its flag emoji — "AU" → 🇦🇺 — by mapping each letter to its regional indicator. */
const flagEmoji = (country: CountryCode): string =>
  String.fromCodePoint(...[...country].map((letter) => 0x1f1e6 + letter.charCodeAt(0) - 65));

/**
 * The masked countries as the coverage modal lists them. The flag and the dial code are DERIVED
 * from the ISO code, never stored beside it: the constant above stays the one edit when coverage
 * changes, and a dial code can never quietly become the thing we match on.
 */
export const maskedCountryRows = (): { code: CountryCode; flag: string; name: string; callingCode: string }[] =>
  MASKED_COUNTRIES.map((code) => ({
    code,
    flag: flagEmoji(code),
    name: (locale as Record<string, string>)[code] ?? code,
    callingCode: getCountryCallingCode(code),
  }));
